import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Circle, ExternalLink, Menu, X } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/useAuth';
import { useSupervisorReportingFilters } from '../contexts/SupervisorReportingFiltersContext';
import { useNavShellFilters } from '../contexts/NavShellFiltersContext';
import { QUALITY_WORK_LINKS } from '../lib/compliance_constants';
import { ETHIC_TEAM_LABEL } from '../lib/complianceCategoryTypes';
import { isTGOwner } from '../lib/occConstants';
import { isManagerAuditorOrAdminForTeamDashboard } from '../lib/privilegedIncidentNavRole';
import { normalizeDepartmentId } from '../utils/normalizeIds';
import { MultiSelectDropdown } from './ui/MultiSelectDropdown';

const SUPERVISOR_REPORTING_TAB = 'รายงานรวม';

const getPalette = () => ({
  primary: '#6F4CF6',
  green: '#22C55E',
  amber: '#F59E0B',
  red: '#EF4444',
  blue: '#3B82F6',
} as const);

const QUALITY_WORK_NAV_LABEL = "งานคุณภาพ";

export const TODO_QUALITY_GRADIENT =
  "linear-gradient(90deg, rgba(107,188,219,1) 0%, rgba(163,230,191,1) 50%, rgba(237,221,83,1) 100%)";

function sortQualityLinks<T extends { label: string; tier?: string }>(links: readonly T[]): T[] {
  return [...links].sort((a, b) => {
    const aTodo = a.tier === "todo-quality" ? 0 : 1;
    const bTodo = b.tier === "todo-quality" ? 0 : 1;
    if (aTodo !== bTodo) return aTodo - bTodo;
    if (aTodo === 0) return a.label.localeCompare(b.label);
    return 0;
  });
}

export const sortedQualityWorkLinks = sortQualityLinks(QUALITY_WORK_LINKS);

/** Header avatar role line: Thai labels for Manager by Firebase Auth UID (display only). */
const MANAGER_ROLE_DISPLAY_BY_UID: Record<string, string> = {
  yIqRKhdicnYUWpuJiyE2WGeqO8q2: "ผช.หนพ. บริหาร",
  UFQJ74w0NUckCsdwRAxYs1Hoy862: "ผช.หนพ. วิชาการ",
  wLgTjM0Tr0RA2b3qSyRPFG2fcZt1: "ผช.หนพ. บริการ OPD",
  "1FacQtVJ0gZPAgXd5fJjtJh8eWu2": "ผช.หนพ. บริการ IPD",
  asOI8v3nkhUelS2Q1EK6VplbvY52: "หัวหน้าพยาบาล",
};

export type NavigationTab = { label: string; disabled?: boolean; helperText?: string };

export type QualityWorkLink = { label: string; url: string; tier?: "todo-quality" };

export type AppShellOutletContext = {
  appShellShowsMainNav?: boolean;
  /** Sticky shell team filter row is visible — hide duplicate inline dept controls and sync shell → reporting state. */
  shellTeamDeptFilterActive?: boolean;
  /** Sticky /report nurse_notes dept filter visible — control lives in AuthenticatedLayout. */
  shellReportNurseNotesDeptFilterActive?: boolean;
  /** Sticky /report ethicSurveys or orgClimateSurveys dept filter visible — control lives in AuthenticatedLayout. */
  shellReportEthicDeptFilterActive?: boolean;
};

function sortNormDeptKey(ids: string[]): string {
  return [...ids].map((id) => normalizeDepartmentId(id)).filter(Boolean).sort().join("\0");
}

/** Mirrors active รายงานรวม category to the shell so AuthenticatedLayout can show /report-only sticky filters. */
export function ReportComplianceSummaryCategoryBridge() {
  const filters = useSupervisorReportingFilters();
  const { setReportComplianceSummaryCategoryId } = useNavShellFilters();

  React.useEffect(() => {
    const id = filters?.categoryId?.trim() ? filters.categoryId.trim() : null;
    setReportComplianceSummaryCategoryId(id);
    return () => setReportComplianceSummaryCategoryId(null);
  }, [filters?.categoryId, setReportComplianceSummaryCategoryId]);

  return null;
}

/** Clear shell nurse_notes dept selection when user switches category away from nurse_notes. */
export function ReportShellNurseNotesNavResetSync() {
  const filters = useSupervisorReportingFilters();
  const { setReportNurseNotesDeptNavFilterIds } = useNavShellFilters();
  const categoryId = filters?.categoryId;

  React.useEffect(() => {
    if (categoryId !== "nurse_notes") {
      setReportNurseNotesDeptNavFilterIds([]);
    }
  }, [categoryId, setReportNurseNotesDeptNavFilterIds]);

  return null;
}

/** Clear shell ethic/org-climate dept selection when user switches category away. */
export function ReportShellEthicNavResetSync() {
  const filters = useSupervisorReportingFilters();
  const { setReportEthicDeptNavFilterIds } = useNavShellFilters();
  const categoryId = filters?.categoryId;

  React.useEffect(() => {
    if (categoryId !== 'ethicSurveys' && categoryId !== 'orgClimateSurveys') {
      setReportEthicDeptNavFilterIds([]);
    }
  }, [categoryId, setReportEthicDeptNavFilterIds]);

  return null;
}

/** Sticky shell dept filter → shared reporting nurse_notes filter state. */
export function ReportShellNurseNotesDeptFilterSync() {
  const filters = useSupervisorReportingFilters();
  const { reportNurseNotesDeptNavFilterIds } = useNavShellFilters();

  React.useEffect(() => {
    if (!filters || filters.categoryId !== "nurse_notes") return;
    const next = reportNurseNotesDeptNavFilterIds;
    if (sortNormDeptKey(next) === sortNormDeptKey(filters.nurseNotesDeptFilter)) return;
    filters.setNurseNotesDeptFilter(next);
  }, [
    reportNurseNotesDeptNavFilterIds,
    filters?.categoryId,
    filters?.nurseNotesDeptFilter,
    filters?.setNurseNotesDeptFilter,
  ]);

  return null;
}

/**
 * Sticky shell มุมมอง / เดือนรายงาน ↔ shared nurse_notes filters.
 * The shell controls live above this provider, so they cannot call setView directly.
 */
export function ReportShellNurseNotesPeriodSync() {
  const filters = useSupervisorReportingFilters();
  const {
    reportNurseNotesView,
    setReportNurseNotesView,
    reportNurseNotesReportingMonth,
    setReportNurseNotesReportingMonth,
  } = useNavShellFilters();
  const shellStamp = `${reportNurseNotesView}|${reportNurseNotesReportingMonth}`;
  const filterStamp =
    filters?.categoryId === 'nurse_notes' ? `${filters.view}|${filters.reportingMonth}` : '';
  const lastPushed = React.useRef('');

  React.useEffect(() => {
    if (!filters || filters.categoryId !== 'nurse_notes') return;
    if (shellStamp === filterStamp) {
      lastPushed.current = shellStamp;
      return;
    }
    if (lastPushed.current === shellStamp) {
      setReportNurseNotesView(filters.view);
      setReportNurseNotesReportingMonth(filters.reportingMonth);
      lastPushed.current = filterStamp;
      return;
    }
    filters.setView(reportNurseNotesView);
    filters.setReportingMonth(reportNurseNotesReportingMonth);
    lastPushed.current = shellStamp;
  }, [
    shellStamp,
    filterStamp,
    filters,
    reportNurseNotesView,
    reportNurseNotesReportingMonth,
    setReportNurseNotesView,
    setReportNurseNotesReportingMonth,
  ]);

  return null;
}

/** Sticky shell dept filter → shared reporting ethic/org-climate filter state. */
export function ReportShellEthicDeptFilterSync() {
  const filters = useSupervisorReportingFilters();
  const { reportEthicDeptNavFilterIds } = useNavShellFilters();

  React.useEffect(() => {
    if (
      !filters ||
      (filters.categoryId !== 'ethicSurveys' && filters.categoryId !== 'orgClimateSurveys')
    ) {
      return;
    }
    const next = reportEthicDeptNavFilterIds;
    if (sortNormDeptKey(next) === sortNormDeptKey(filters.ethicDeptFilter)) return;
    filters.setEthicDeptFilter(next);
  }, [
    reportEthicDeptNavFilterIds,
    filters?.categoryId,
    filters?.ethicDeptFilter,
    filters?.setEthicDeptFilter,
  ]);

  return null;
}

/** When AuthenticatedLayout shows `TeamDepartmentFilterBar`, keep `ethicDeptFilter` aligned with `teamDeptNavFilterIds`. */
export function ReportShellTeamDeptFilterSync({ active }: { active: boolean }) {
  const filters = useSupervisorReportingFilters();
  const { teamDeptNavFilterIds } = useNavShellFilters();

  React.useEffect(() => {
    if (!active || !filters) return;
    if (filters.categoryId !== 'ethicSurveys' && filters.categoryId !== 'orgClimateSurveys') return;
    const next = teamDeptNavFilterIds;
    if (sortNormDeptKey(next) === sortNormDeptKey(filters.ethicDeptFilter)) return;
    filters.setEthicDeptFilter(next);
  }, [active, teamDeptNavFilterIds, filters?.categoryId, filters?.ethicDeptFilter, filters?.setEthicDeptFilter]);

  return null;
}

export function HeaderBar({
  activeTab,
  onSelectTab,
  tabs,
  qualityWorkDropdown = false,
  qualityWorkLinks = [],
  /** When true, only render compliance sub-tabs (รายงานรวม / KPIs / …) — AuthenticatedLayout already shows NiData, user, งานคุณภาพ */
  embedTabBarOnly = false,
}: {
  activeTab: string;
  onSelectTab: (tab: string) => void;
  tabs: NavigationTab[];
  qualityWorkDropdown?: boolean;
  qualityWorkLinks?: readonly QualityWorkLink[];
  embedTabBarOnly?: boolean;
}) {
  const { authUser, auth, firestoreUser, isAdmin, roleContext } = useAuth();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const sortedLinks = React.useMemo(() => sortQualityLinks(qualityWorkLinks ?? []), [qualityWorkLinks]);

  const isPrivilegedAdmin =
    authUser?.email?.toLowerCase() === "5650414@nidata.com" &&
    (authUser?.uid?.startsWith("FDbScvYwK9NguMvSOflJmVrysi12") ?? false);

  const handleSignOut = React.useCallback(() => {
    setMenuOpen(false);
    signOut(auth).catch((error) => console.error("Sign out error", error));
  }, [auth]);

  const hasNNTeamRole = React.useMemo(() => {
    const roleTokens = [roleContext?.teamRole, ...(roleContext?.teamRoles ?? [])]
      .filter(Boolean)
      .map((r) => {
        if (typeof r === "string") return r.toUpperCase();
        const o = r as { role?: string; teamCode?: string; teamId?: string };
        return (o.role ?? o.teamCode ?? o.teamId ?? "").toUpperCase();
      });
    return roleTokens.includes("NN");
  }, [roleContext?.teamRole, roleContext?.teamRoles]);

  const hasTeamAccess = (roleContext?.teamAllowedCategoryIds ?? []).filter(Boolean).length > 0;
  const hasEthicTeamRole = (roleContext?.teamAllowedCategoryIds ?? []).includes("ethicSurveys");
  const hasTGProjectAccess =
    isTGOwner(authUser?.uid ?? null) ||
    !!roleContext?.hasOCCTeamRole ||
    isManagerAuditorOrAdminForTeamDashboard(roleContext ?? null, isAdmin);

  const menuItems = React.useMemo(() => {
    const items: { key: string; label: string; to?: string; onClick?: () => void }[] = [];

    if (isPrivilegedAdmin || isAdmin) {
      items.push(
        { key: "user-admin", label: "User Administration", to: "/roles" },
        {
          key: "department-admin",
          label: "Department Administration",
          to: "/departments",
        },
        {
          key: "compliance-categories",
          label: "Compliance Categories",
          to: "/compliance-categories",
        },
      );
    }

    if (hasTeamAccess && hasNNTeamRole) {
      items.push({ key: "team-nurse-note", label: "บันทึกทางการพยาบาล", to: "/team" });
    }
    if (hasEthicTeamRole) {
      items.push({ key: "team-ethic", label: ETHIC_TEAM_LABEL, to: "/team/ethic" });
    }
    if (hasTGProjectAccess) {
      items.push({ key: "team-tg-project", label: "โครงการ TG", to: "/team/occ" });
    }

    items.push({ key: "sign-out", label: "Sign out", onClick: handleSignOut });
    return items;
  }, [handleSignOut, isAdmin, isPrivilegedAdmin, hasTeamAccess, hasNNTeamRole, hasEthicTeamRole, hasTGProjectAccess]);

  const firestoreDisplayName =
    typeof firestoreUser?.displayName === "string" ? firestoreUser.displayName.trim() : "";
  const authDisplayName =
    typeof authUser?.displayName === "string" ? authUser.displayName.trim() : "";
  const canViewSensitiveDetails = isPrivilegedAdmin || isAdmin;
  const userEmail = authUser?.email ?? "";
  const baseDisplayName = firestoreDisplayName || authDisplayName || "";
  const isEmailDisplayName =
    Boolean(userEmail) && baseDisplayName.toLowerCase() === userEmail.toLowerCase();
  const userDisplayName =
    baseDisplayName && !(isEmailDisplayName && !canViewSensitiveDetails)
      ? baseDisplayName
      : canViewSensitiveDetails && userEmail
        ? userEmail
        : "Member";
  const userInitial = userDisplayName.trim()?.[0]?.toUpperCase() || "?";

  const roleLabel = (() => {
    if (canViewSensitiveDetails) return "Admin";
    const uid = authUser?.uid ?? "";
    const pos = (roleContext?.positionRole ?? "").toUpperCase();
    const gen = (roleContext?.generalRole ?? "").toUpperCase();
    if (pos === "SUPERVISOR_NURSE" || pos === "SN" || gen === "SUPERVISOR_NURSE" || gen === "SUPERVISOR") {
      return "ผู้ตรวจการพยาบาล";
    }
    if (pos === "HEAD_WARD_NURSE" || pos === "HWN" || gen === "HEAD_WARD_NURSE") {
      return "หัวหน้าหอผู้ป่วย";
    }
    if (pos === "AUDITOR" || gen === "AUDITOR") return "Auditor";
    if (gen === "MANAGER") {
      const managerOverride = uid ? MANAGER_ROLE_DISPLAY_BY_UID[uid] : undefined;
      return managerOverride ?? "Manager";
    }
    return "Member";
  })();

  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const mobileNavRef = React.useRef<HTMLDivElement | null>(null);

  const [qualityWorkOpen, setQualityWorkOpen] = React.useState(false);
  const qualityWorkRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!mobileNavOpen) return;

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!mobileNavRef.current || !target) return;
      if (!mobileNavRef.current.contains(target)) {
        setMobileNavOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [mobileNavOpen]);

  React.useEffect(() => {
    if (!qualityWorkOpen) return;
    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!qualityWorkRef.current || !target) return;
      if (!qualityWorkRef.current.contains(target)) setQualityWorkOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [qualityWorkOpen]);

  if (embedTabBarOnly) {
    return (
      <header
        className="border-b border-slate-200/80 bg-white/60 px-2 py-2 sm:px-4"
        aria-label="รายงานและ KPIs"
      >
        <nav
          className="-mx-1 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 overflow-x-auto px-1 sm:gap-x-5 md:gap-6"
          role="tablist"
        >
          {tabs.map((t) => {
            const isActive = activeTab === t.label;
            const isDisabled = t.disabled;
            return (
              <button
                key={t.label}
                type="button"
                role="tab"
                aria-current={isActive ? "page" : undefined}
                onClick={() => !isDisabled && onSelectTab(t.label)}
                aria-disabled={isDisabled}
                className={`shrink-0 whitespace-nowrap text-sm pb-1.5 border-b-2 ${
                  isActive
                    ? "border-purple-600 text-slate-900 font-medium"
                    : isDisabled
                      ? "border-transparent text-slate-400 cursor-not-allowed"
                      : "border-transparent text-slate-600 hover:text-slate-900"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </nav>
      </header>
    );
  }

  return (
    <header className="h-[80px] flex items-center justify-between px-6 bg-transparent gap-4">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold text-slate-800">NiData</h1>
        <span className="inline-block w-2 h-2 rounded-full bg-red-500" aria-hidden />
      </div>
      <nav className="hidden md:flex items-center gap-6 flex-1 justify-center">
        {tabs.map((t) => {
          const isActive = activeTab === t.label;
          const isDisabled = t.disabled;
          return (
            <button
              key={t.label}
              type="button"
              aria-current={isActive ? "page" : undefined}
              onClick={() => !isDisabled && onSelectTab(t.label)}
              aria-disabled={isDisabled}
              className={`text-sm pb-2 border-b-2 ${
                isActive
                  ? "border-purple-600 text-slate-900"
                  : isDisabled
                    ? "border-transparent text-slate-400 cursor-not-allowed"
                    : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              {t.label}
              {isActive && <span className="sr-only"> (current page)</span>}
            </button>
          );
        })}
        {qualityWorkDropdown && sortedLinks.length > 0 && (
          <div className="relative" ref={qualityWorkRef}>
            <button
              type="button"
              onClick={() => setQualityWorkOpen((prev) => !prev)}
              aria-expanded={qualityWorkOpen}
              aria-haspopup="true"
              className="text-sm pb-2 border-b-2 border-transparent text-slate-500 hover:text-slate-800 flex items-center gap-1"
            >
              {QUALITY_WORK_NAV_LABEL}
              <ChevronDown className={`w-4 h-4 transition-transform ${qualityWorkOpen ? "rotate-180" : ""}`} />
            </button>
            {qualityWorkOpen && (
              <div
                role="menu"
                className="absolute left-0 top-full mt-1 min-w-[240px] rounded-2xl border border-slate-200 bg-white p-2 shadow-xl z-20"
              >
                {sortedLinks.map((item) => {
                  const isTodo = "tier" in item && item.tier === "todo-quality";
                  return (
                    <a
                      key={item.url}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      role="menuitem"
                      className={`flex items-center gap-3 w-full rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 ${isTodo ? "text-slate-900" : "text-slate-800 hover:bg-slate-50"}`}
                      style={isTodo ? { background: TODO_QUALITY_GRADIENT } : undefined}
                      aria-label={`Open ${item.label}`}
                      onClick={() => setQualityWorkOpen(false)}
                    >
                      <span className="flex-1">{item.label}</span>
                      <ExternalLink className={`w-4 h-4 shrink-0 ${isTodo ? "text-slate-700" : "text-slate-500"}`} aria-hidden />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </nav>
      <div className="relative z-10 shrink-0 md:hidden" ref={mobileNavRef}>
        <button
          type="button"
          onClick={() => setMobileNavOpen((prev) => !prev)}
          className="flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-md hover:border-slate-400 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          aria-expanded={mobileNavOpen}
          aria-controls="mobile-nav-menu"
          aria-label="เปิดเมนูนำทาง"
        >
          <Menu className="h-5 w-5 shrink-0" aria-hidden />
          เมนู
        </button>
        {mobileNavOpen ? (
          <div
            id="mobile-nav-menu"
            className="absolute left-0 top-full mt-2 w-56 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl z-10"
          >
            <div className="flex flex-col">
              {tabs.map((t) => {
                const isActive = activeTab === t.label;
                const isDisabled = t.disabled;
                return (
                  <button
                    key={t.label}
                    type="button"
                    onClick={() => {
                      if (!isDisabled) {
                        onSelectTab(t.label);
                        setMobileNavOpen(false);
                      }
                    }}
                    disabled={isDisabled}
                    className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm ${
                      isActive
                        ? "bg-purple-50 text-purple-700"
                        : isDisabled
                          ? "text-slate-400 cursor-not-allowed"
                          : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {t.label}
                    {isActive ? <span className="text-xs text-purple-600">กำลังใช้งาน</span> : null}
                  </button>
                );
              })}
              {qualityWorkDropdown && sortedLinks.length > 0 && (
                <>
                  <div className="my-1 border-t border-slate-200" />
                  <div className="px-2 py-1 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    {QUALITY_WORK_NAV_LABEL}
                  </div>
                  {sortedLinks.map((item) => {
                    const isTodo = "tier" in item && item.tier === "todo-quality";
                    return (
                      <a
                        key={item.url}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm ${isTodo ? "font-semibold text-slate-900" : "text-slate-700 hover:bg-slate-50"}`}
                        style={isTodo ? { background: TODO_QUALITY_GRADIENT } : undefined}
                        onClick={() => setMobileNavOpen(false)}
                      >
                        <span className="flex-1">{item.label}</span>
                        <ExternalLink className={`w-4 h-4 shrink-0 ${isTodo ? "text-slate-700" : "text-slate-500"}`} aria-hidden />
                      </a>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>
      <div className="relative">
        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          className="flex items-center gap-2 rounded-full bg-white/80 px-3 py-2 shadow-sm border border-slate-200 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-purple-600 text-white font-semibold">
            {userInitial}
          </span>
          <div className="hidden sm:block text-left">
            <p className="text-sm font-semibold text-slate-800 leading-tight">{userDisplayName}</p>
            {canViewSensitiveDetails && userEmail ? (
              <p className="text-xs text-slate-500 leading-tight truncate">{userEmail}</p>
            ) : null}
            <p className="text-xs text-slate-500 leading-tight">{roleLabel}</p>
          </div>
        </button>
        {menuOpen && (
          <div className="absolute right-0 mt-2 w-64 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl z-10">
            <div className="px-3 py-2">
              <p className="text-sm font-semibold text-slate-800">{userDisplayName}</p>
              {canViewSensitiveDetails && userEmail ? (
                <p className="text-xs text-slate-500 break-all">{userEmail}</p>
              ) : null}
              {canViewSensitiveDetails && authUser?.uid ? (
                <p className="text-xs text-slate-500 break-all">UID: {authUser.uid}</p>
              ) : null}
            </div>
            <div className="my-2 h-px bg-slate-100" aria-hidden />
            <div className="flex flex-col">
              {menuItems.map((item) => {
                if (item.onClick) {
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={item.onClick}
                      className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      {item.label}
                    </button>
                  );
                }
                return (
                  <Link
                    key={item.key}
                    to={item.to}
                    onClick={() => setMenuOpen(false)}
                    className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
